import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSwipeable } from 'react-swipeable'

// Components
import CarouselItem from '../CarouselItem'
import Modal from '../CarouselModal'
import CarouselNav from '../CarouselNav'
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import ContainedWrap from '../ContainedWrap'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'

// Styles
import {
  CarouselWrapper,
  CarouselMain,
  CarouselHeader,
  CarouselContainer,
  CarouselHeading,
  CarouselTrack,
  CarouselDesktopFeaturedNav,
  CarouselMobileFeaturedNav,
} from './index.style'
import { breakpoints } from '../../styles/vars/breakpoints.style'

const Carousel = ({
  id,
  featured,
  heading,
  carouselItems,
  linkId,
  nextSlideLabel,
  prevSlideLabel,
}) => {
  const [currentIndex, setCurrentIndex] = useState(carouselItems.length)
  const [repeatingItems, setRepeatingItems] = useState(null)
  const [transitions, setTransitions] = useState(true)

  const [modalItem, setModalItem] = useState(-1)
  const [modalOpen, setModalOpen] = useState(false)

  const items = useRef([])
  const itemCount = useRef(carouselItems.length)
  const repeatingItemCount = useRef(null)

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (window.innerWidth < breakpoints.tabletL) {
        nextSlide()
      }
    },
    onSwipedRight: () => {
      if (window.innerWidth < breakpoints.tabletL) {
        prevSlide()
      }
    },
  })

  const carouselRef = useRef(null)

  const transitionTimeout = useRef(null)

  const appendArrayToArray = (arr1, arr2) => {
    for (let x = 0; x < arr1.length; x++) {
      arr2.push(arr1[x])
    }
  }

  useEffect(() => {
    items.current = []
    appendArrayToArray(carouselItems, items.current)
    appendArrayToArray(carouselItems, items.current)
    appendArrayToArray(carouselItems, items.current)

    repeatingItemCount.current = items.current.length

    setRepeatingItems(items.current)
  }, [carouselItems])

  const prevSlide = useCallback(() => {
    if (transitions) return
    setTransitions(true)
    setCurrentIndex(currentIndex => currentIndex - 1)
  }, [transitions])

  const nextSlide = useCallback(() => {
    if (transitions) return
    setTransitions(true)
    setCurrentIndex(currentIndex => currentIndex + 1)
  }, [transitions])

  const resetSlide = useCallback(() => {
    setCurrentIndex(carouselItems.length)
  }, [carouselItems])

  const enableSlide = useCallback(() => {
    transitionTimeout.current = null
    setTransitions(false)
    if (currentIndex === 0 || currentIndex === itemCount.current * 2)
      resetSlide()
  }, [currentIndex, resetSlide])

  useEffect(() => {
    transitionTimeout.current = setTimeout(enableSlide, 1200)

    return () => {
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current)
    }
  }, [currentIndex, enableSlide])

  return (
    <>
      <ScrollSection>
        <CarouselWrapper id={linkId && linkId}>
          <Spacer size={[120, featured ? 177 : 250]} />
          <CarouselMain ref={carouselRef} {...handlers}>
            <Container>
              <CarouselHeader>
                <Grid>
                  <GridItem
                    tiny={featured ? 10 : 7}
                    tabletP={8}
                    tabletPStart={featured ? 1 : 2}
                    desk={featured ? 4 : 8}
                    deskStart={featured ? 9 : 2}
                    deskL={featured ? 3 : 8}
                    deskLStart={featured ? 9 : 2}
                  >
                    <ContainedWrap size={featured ? 380 : 200} center={false}>
                      <CarouselHeading>
                        <RichText
                          id={`${id}-heading`}
                          content={heading}
                          forceHeading
                          delay={0}
                          delayEm={true}
                        />
                        {featured && (
                          <CarouselDesktopFeaturedNav>
                            <Spacer size={20} />
                            <CarouselNav
                              id={`${id}-nav`}
                              delay={0.8}
                              prevEvent={prevSlide}
                              nextEvent={nextSlide}
                              nextSlideLabel
                              prevSlideLabel
                            />
                          </CarouselDesktopFeaturedNav>
                        )}
                      </CarouselHeading>
                    </ContainedWrap>
                  </GridItem>
                  {!featured && (
                    <GridItem
                      tiny={4}
                      tinyStart={9}
                      tinyAlign={`flex-end`}
                      tabletP={2}
                      tabletPStart={11}
                      tabletL={2}
                      tabletLStart={10}
                    >
                      <CarouselNav
                        id={`${id}-nav-featured`}
                        align={`flex-end`}
                        delay={0.8}
                        prevEvent={prevSlide}
                        nextEvent={nextSlide}
                      />
                    </GridItem>
                  )}
                </Grid>
              </CarouselHeader>

              <Spacer size={featured ? 30 : 60} />

              <CarouselContainer featured={featured}>
                <CarouselTrack featured={featured}>
                  {repeatingItems &&
                    React.Children.toArray(
                      repeatingItems.map((item, itemIndex) => {
                        return (
                          <CarouselItem
                            {...item}
                            id={id + '-item-' + itemIndex}
                            featured={featured}
                            delay={itemIndex * 0.1 - 1}
                            index={itemIndex - currentIndex}
                            transitions={transitions}
                            event={() => {
                              if (featured && modalItem < 0) {
                                setModalItem(itemIndex)
                                setModalOpen(true)
                              }
                            }}
                          />
                        )
                      })
                    )}
                </CarouselTrack>
              </CarouselContainer>

              {featured && (
                <CarouselMobileFeaturedNav>
                  <Spacer size={20} />
                  <Grid>
                    <GridItem
                      tiny={4}
                      tinyStart={5}
                      tinyAlign={`center`}
                      tabletP={2}
                      tabletPStart={6}
                      tabletL={2}
                      tabletLStart={10}
                      tabletLAlign={`flex-end`}
                    >
                      <CarouselNav
                        id={`${id}-mobile`}
                        align={`center`}
                        delay={2}
                        prevEvent={prevSlide}
                        nextEvent={nextSlide}
                      />
                    </GridItem>
                  </Grid>
                </CarouselMobileFeaturedNav>
              )}
            </Container>
          </CarouselMain>
          <Spacer size={[100, featured ? 100 : 240]} />
        </CarouselWrapper>
      </ScrollSection>
      {featured && (
        <>
          <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            setModalItem={setModalItem}
            activeItem={modalItem !== -1 ? items.current[modalItem] : null}
          />
        </>
      )}
    </>
  )
}

Carousel.propTypes = {
  id: PropTypes.string.isRequired,
  featured: PropTypes.bool,
  heading: PropTypes.object,
  carouselItems: PropTypes.array,
}

export default Carousel
